/**
 * Reading and writing a child's own ledger — the bank.
 *
 * Same shape as `fuel.js`, and deliberately so: balances are projected here in
 * the client from the entries, never read from a column, because a stored
 * counter is a number that can disagree with its own history and nobody can
 * tell afterwards which one lied.
 *
 * The one structural thing this module has to keep straight is the firewall
 * between the two ledgers. `child_entries` is the child's side; family Fuel
 * lives in `envelope_entries` and never comes through here. `project()` drops
 * anything without a `childId` as a second line of defence, but the first one
 * is that these two tables are never read into the same array.
 *
 * Issuance of the savings bonus and of the allowance is server-side, like
 * refills: this module reads `interest` and `allowance` rows and never writes
 * them. What a client may write is the four things a person actually does — put
 * money in, sort it, hand it over, and correct a mistake — plus the two rules
 * that tell the job what to issue, which are settings rather than money.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase.js';
import {
  allocationEntries, depositEntry, project, reversalEntry, settlementEntry,
} from '../domain/ledger.js';
import { outstandingObligation } from '../domain/liability.js';
import { currentSavingsRule, withSavingsDefaults } from '../domain/savings.js';
import { ALLOWANCE_DEFAULTS, withDefaults as withAllowanceDefaults } from '../domain/allowance.js';
import { toDateOnly } from '../domain/periods.js';

/**
 * Display vocabulary, kept in one place so the select a parent picks from and
 * the history line they read afterwards say the same word. Display-only: no
 * financial value depends on any of it (CLAUDE.md, invariants).
 */
export const BUCKET_LABEL = Object.freeze({ spend: 'Spend', save: 'Save', give: 'Give' });
/** "Every week" — the word that names a cadence in a heading or a select. */
export const CADENCE_LABEL = Object.freeze({
  weekly: 'Every week', biweekly: 'Every two weeks', monthly: 'Every month',
});
/**
 * "$5.00 a week" — the same cadence at the end of an amount.
 *
 * Two forms rather than one because "$10.00 a two weeks" is not English, and a
 * screen that reads wrong in one of three settings reads wrong for whichever
 * family picked it.
 */
export const CADENCE_RATE = Object.freeze({
  weekly: 'a week', biweekly: 'every two weeks', monthly: 'a month',
});
export const METHOD_LABEL = Object.freeze({
  cash: 'cash',
  purchased: 'bought it for them',
  transferred: 'transferred',
  donated: 'donated',
});

/** A child with no entries yet still has to render a balance. */
export const EMPTY_BALANCE = Object.freeze({
  spend: 0, save: 0, give: 0, unallocated: 0, total: 0,
});

/**
 * Postgres returns snake_case; the domain speaks camelCase and must not be bent
 * to match a transport. Mapped at the boundary, once — same as `fuel.js`.
 *
 * A `reversal` row stores the id it corrects in `reason_id`, because the table
 * has one column for "what this points at" and a reversal points at an entry.
 * It comes back out as `reversesId` so the row matches the shape
 * `reversalEntry` produces, and so `reasonId` never quietly means two things.
 */
const toEntry = (e) => ({
  id: e.id,
  childId: e.child_id,
  currency: e.currency,
  bucket: e.bucket,
  amountCents: e.amount_cents,
  reasonType: e.reason_type,
  reasonId: e.reason_type === 'reversal' ? undefined : e.reason_id,
  actorId: e.actor_id,
  note: e.note,
  snapshot: e.snapshot,
  occurredAt: e.occurred_at,
  ...(e.reason_type === 'reversal' ? { reversesId: e.reason_id } : {}),
});

/** And back the other way. Also one place, so the two can be read together. */
const entryRow = (e, familyId) => ({
  id: e.id,
  family_id: familyId,
  child_id: e.childId,
  currency: e.currency,
  bucket: e.bucket ?? null,
  amount_cents: e.amountCents,
  reason_type: e.reasonType,
  reason_id: e.reasonId ?? e.reversesId ?? null,
  actor_id: e.actorId ?? null,
  note: e.note ?? null,
  snapshot: e.snapshot ?? null,
  occurred_at: e.occurredAt,
});

const toRule = (r) => withSavingsDefaults({
  id: r.id,
  childId: r.child_id,
  version: r.version,
  annualRatePct: r.annual_rate_pct,
  monthlyCapCents: r.monthly_cap_cents,
  effectiveFrom: r.effective_from,
  createdAt: r.created_at,
});

/** Named once so the read and the write cannot drift into two column lists. */
const ALLOWANCE_COLUMNS = 'id, child_id, version, formula, dollars_per_year_of_age, guaranteed_share, cadence, rounding_cents, floor_cents, ceiling_cents, effective_from, created_at';

/**
 * The allowance rule, snake_case to the shape `src/domain/allowance.js` expects.
 *
 * Every numeric column comes back from PostgREST as a string, and the formula
 * multiplies. `age * "0.50"` is `NaN` in one place and a concatenation in
 * another, so the coercion happens here, at the boundary, once — the same rule
 * the edge function follows on its own side of the wire.
 *
 * A null falls back to the domain default rather than to `Number(null)`, which
 * is 0: a missing ceiling that becomes a $0.00 ceiling is an allowance of
 * nothing, and it would look like the formula rather than like the mapping.
 */
const num = (value, fallback) => (value == null ? fallback : Number(value));

const toAllowanceRule = (r) => withAllowanceDefaults({
  id: r.id,
  childId: r.child_id,
  version: r.version,
  formula: r.formula,
  dollarsPerYearOfAge: num(r.dollars_per_year_of_age, ALLOWANCE_DEFAULTS.dollarsPerYearOfAge),
  guaranteedShare: num(r.guaranteed_share, ALLOWANCE_DEFAULTS.guaranteedShare),
  cadence: r.cadence,
  roundingCents: num(r.rounding_cents, ALLOWANCE_DEFAULTS.roundingCents),
  floorCents: num(r.floor_cents, ALLOWANCE_DEFAULTS.floorCents),
  ceilingCents: num(r.ceiling_cents, ALLOWANCE_DEFAULTS.ceilingCents),
  effectiveFrom: String(r.effective_from).slice(0, 10),
  createdAt: r.created_at,
});

/**
 * The rule that is actually paying today: the latest one that has started.
 *
 * The rows arrive newest-first, so this is the first one whose `effective_from`
 * has arrived. Stricter than the savings read path, which shows the newest rule
 * regardless because it is showing a parent what they configured — here the
 * number is quoted to a child as what they get, so it has to be the number the
 * job will actually issue.
 */
const liveAllowanceRule = (rules, today) =>
  rules.find((r) => r.effectiveFrom <= today) ?? null;

export function useBank(familyId) {
  const [state, setState] = useState({
    loading: true, entries: [], byChild: {}, rules: {}, allowanceRules: {}, outstandingCents: 0,
  });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!familyId) { setState((s) => ({ ...s, loading: false })); return; }

    // One "today" for the whole load, so the rule the preview quotes and the
    // balance beside it cannot come from two different days.
    const today = toDateOnly(new Date());

    const [entryRes, ruleRes, allowanceRes] = await Promise.all([
      // Ordered oldest-first, because that is the order a ledger reads in and
      // the order every projection over it assumes. The history screen reverses
      // it for display rather than the query doing it, so both callers agree
      // about which end is "now".
      supabase.from('child_entries')
        .select('id, child_id, currency, bucket, amount_cents, reason_type, reason_id, actor_id, note, snapshot, occurred_at')
        .eq('family_id', familyId)
        .order('occurred_at', { ascending: true })
        .order('created_at', { ascending: true }),
      // Every version, newest first. A rule is never edited — changing the rate
      // inserts a row — so the live one is simply the first of these, and the
      // older ones are what makes a past bonus entry still readable.
      supabase.from('savings_rules')
        .select('id, child_id, version, annual_rate_pct, monthly_cap_cents, effective_from, created_at')
        .eq('family_id', familyId)
        .order('effective_from', { ascending: false })
        .order('created_at', { ascending: false }),
      // Same shape, same reason: insert-only, so every version is history and
      // the live one is picked from the list rather than read from a column.
      supabase.from('allowance_rules')
        .select(ALLOWANCE_COLUMNS)
        .eq('family_id', familyId)
        .order('effective_from', { ascending: false })
        .order('created_at', { ascending: false }),
    ]);

    const firstError = entryRes.error || ruleRes.error || allowanceRes.error;
    if (firstError) { setError(firstError.message); setState((s) => ({ ...s, loading: false })); return; }

    const entries = (entryRes.data ?? []).map(toEntry);

    const byChild = {};
    for (const e of entries) {
      if (!e.childId) continue;
      (byChild[e.childId] ??= { entries: [], balance: EMPTY_BALANCE }).entries.push(e);
    }
    for (const [childId, group] of Object.entries(byChild)) {
      // Projected per child from that child's own rows. The `childId` argument
      // is still passed even though the array is already filtered: it is the
      // guard that matters, not the filter, and a future caller handing it a
      // mixed array should get the same answer.
      group.balance = project(group.entries, { childId });
    }

    // Which rate applies is answered by the domain, not by the ORDER BY above.
    // `currentSavingsRule` is the same comparison the issuing job uses, and the
    // two sides of the wire disagreeing about the live rule is exactly the bug
    // a preview screen cannot show you.
    const byChildRules = {};
    for (const row of ruleRes.data ?? []) (byChildRules[row.child_id] ??= []).push(toRule(row));
    const rules = Object.fromEntries(
      Object.entries(byChildRules).map(([id, list]) => [id, currentSavingsRule(list)]),
    );

    const byChildAllowance = {};
    for (const row of allowanceRes.data ?? []) {
      (byChildAllowance[row.child_id] ??= []).push(toAllowanceRule(row));
    }
    const allowanceRules = Object.fromEntries(
      Object.entries(byChildAllowance).map(([id, list]) => [id, liveAllowanceRule(list, today)]),
    );

    setState({
      loading: false,
      entries,
      byChild,
      rules,
      allowanceRules,
      // What the parent owes, in one number, across every child. See
      // PLAN_REVIEW.md §7: balances in this app are promises, not deposits,
      // and without the aggregate they only ever grow.
      outstandingCents: outstandingObligation(
        Object.fromEntries(Object.entries(byChild).map(([id, g]) => [id, g.balance.total])),
      ),
    });
  }, [familyId]);

  useEffect(() => { load(); }, [load]);

  return { ...state, error, reload: load, today: toDateOnly(new Date()) };
}

/**
 * Money a parent puts in by hand: birthday cash, a grandparent's cheque, the
 * jar that existed before the app did.
 *
 * It lands unallocated on purpose — see `depositEntry`. The id is keyed on a
 * fresh `depositId`, and the insert ignores duplicates, so a double-tapped
 * button or a retried request whose response was lost deposits once.
 */
export async function deposit({ familyId, childId, cents, note, actorId, occurredAt }) {
  const entry = depositEntry({
    childId, cents, note, actorId, occurredAt, depositId: crypto.randomUUID(),
  });
  const { error } = await supabase.from('child_entries')
    .insert(entryRow(entry, familyId), { onConflict: 'id', ignoreDuplicates: true });
  if (error) throw new Error(error.message);
  return entry;
}

/**
 * The child sorts their own money into Spend, Save and Give.
 *
 * All three rows go in one call: they share a `reasonId` and are one act, and a
 * partial allocation — two buckets credited, the third lost to a dropped
 * request — would leave the ledger summing to less than the child had.
 */
export async function allocate({ familyId, childId, cents, weights, actorId, occurredAt }) {
  const entries = allocationEntries({
    childId, cents, weights, occurredAt, reasonId: crypto.randomUUID(),
  }).map((e) => ({ ...e, actorId }));
  if (!entries.length) return [];
  const { error } = await supabase.from('child_entries')
    .insert(entries.map((e) => entryRow(e, familyId)), { onConflict: 'id', ignoreDuplicates: true });
  if (error) throw new Error(error.message);
  return entries;
}

/**
 * Money actually leaving, because a parent handed it over.
 *
 * `remainderCents` is the honest half: $4.37 owed and $4.25 in coins on the
 * counter is not a rounding error to swallow, it is 12¢ the child is still
 * owed, and it stays on the ledger. The caller is expected to say so.
 */
export async function settle({
  familyId, childId, bucket, owedCents, paidCents, method, note, actorId, occurredAt,
}) {
  const { entry, remainderCents } = settlementEntry({
    childId, bucket, owedCents, paidCents, method, note, actorId, occurredAt,
    requestId: crypto.randomUUID(),
  });
  const { error } = await supabase.from('child_entries')
    .insert(entryRow(entry, familyId), { onConflict: 'id', ignoreDuplicates: true });
  if (error) throw new Error(error.message);
  return { entry, remainderCents };
}

/**
 * Correcting a mistake, by writing a new entry rather than removing an old one.
 *
 * The reason is required by the domain and required here too: a correction the
 * family can't read is indistinguishable from money going missing.
 */
export async function reverse({ familyId, entry, reason, actorId, occurredAt }) {
  const reversal = reversalEntry({ original: entry, actorId, reason, occurredAt });
  const { error } = await supabase.from('child_entries')
    .insert(entryRow(reversal, familyId), { onConflict: 'id', ignoreDuplicates: true });
  if (error) throw new Error(error.message);
  return reversal;
}

/**
 * Set the savings bonus rate.
 *
 * An insert, never an update. History has to stay readable after a rate
 * changes — an entry issued under 5% must still be explainable when the rule
 * says 2% — so the old row stays and the new one supersedes it by date.
 */
export async function setSavingsRule({
  familyId, childId, annualRatePct, monthlyCapCents, effectiveFrom, version,
}) {
  const rule = withSavingsDefaults({ annualRatePct, monthlyCapCents, effectiveFrom });
  const { data, error } = await supabase.from('savings_rules').insert({
    family_id: familyId,
    child_id: childId,
    version: version ?? rule.version ?? 1,
    annual_rate_pct: rule.annualRatePct,
    monthly_cap_cents: rule.monthlyCapCents ?? null,
    effective_from: effectiveFrom,
  }).select('id, child_id, version, annual_rate_pct, monthly_cap_cents, effective_from, created_at').single();
  if (error) throw new Error(error.message);
  return toRule(data);
}

/**
 * Set the allowance.
 *
 * An insert, never an update, for the reason the savings rule is: an entry
 * issued at $0.50 a year of age must still be explainable after a parent moves
 * to $0.75, and the snapshot on the entry is only half of that — the other half
 * is the rule row it names still existing.
 *
 * The version and the formula are written from the domain's own constants
 * rather than from the form. They are what the entry ids encode, and a screen
 * that could set them is a screen that could re-issue every historical period
 * under fresh ids.
 */
export async function setAllowanceRule({
  familyId, childId, dollarsPerYearOfAge, guaranteedShare, cadence,
  roundingCents, floorCents, ceilingCents, effectiveFrom,
}) {
  const rule = withAllowanceDefaults({
    dollarsPerYearOfAge, guaranteedShare, cadence, roundingCents, floorCents, ceilingCents,
  });
  const { data, error } = await supabase.from('allowance_rules').insert({
    family_id: familyId,
    child_id: childId,
    version: rule.version,
    formula: rule.formula,
    dollars_per_year_of_age: rule.dollarsPerYearOfAge,
    guaranteed_share: rule.guaranteedShare,
    cadence: rule.cadence,
    rounding_cents: rule.roundingCents,
    floor_cents: rule.floorCents,
    ceiling_cents: rule.ceilingCents,
    effective_from: effectiveFrom,
  }).select(ALLOWANCE_COLUMNS).single();
  if (error) throw new Error(error.message);
  return toAllowanceRule(data);
}
